import React from 'react';
import { ApiPick, ApiPickLeg, Last7SeriesEntry } from 'services/types';
import { ReactComponent as TrendUp } from '../../assets/icons/trendUp.svg';
import { ReactComponent as TrendDown } from '../../assets/icons/trendDown.svg';
import { ReactComponent as TrendFire } from '../../assets/icons/trendFire.svg';
import LockPickTM from 'components/LockPickTM/LockPickTM';
import { formatThreshold, formatProbability } from 'utils';
import { t } from '../../helpers/componentHelper';

import './PickLeg.scss';

// Renders the athlete's last-7 games as an ordered strip of 7 slots
// (oldest → newest). Each slot is hit / miss / absent. Series arrives
// most-recent-first from the API so we reverse for left-to-right display.
// Left blank when no series is present (team legs or pre-migration picks).
const Last7Indicator = ({
  series,
  games,
  hits,
}: {
  series: Last7SeriesEntry[] | null;
  games: number | null;
  hits: number | null;
}) => {
  if (!series || games === null || hits === null) return null;
  const display = [...series].reverse();
  return (
    <div className={'pickCard__leg-last7'}>
      <span className={'pickCard__leg-last7-label'}>Last 7 games{` (${hits}/${games})`}</span>
      <div className={'pickCard__leg-last7-slots'}>
        {display.map((entry) => {
          const cls =
            entry.hit === true
              ? 'pickCard__leg-last7-slot--hit'
              : entry.hit === false
              ? 'pickCard__leg-last7-slot--miss'
              : 'pickCard__leg-last7-slot--absent';
          return (
            <div key={entry.gameDate} className={'pickCard__leg-last7-slot-container'}>
              <span
                key={entry.gameId}
                className={`pickCard__leg-last7-slot ${cls}`}
                title={`${entry.gameDate} — ${
                  entry.hit === null
                    ? 'no data'
                    : `${entry.statValue ?? '-'} (${entry.hit ? 'hit' : 'miss'})`
                }`}
              />
              <span className={'pickCard__leg-last7-slot-date'}>{entry.statValue}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const getEqIndex = (eqShort: number, eqLong: number) => {
  return eqShort >= 0.2 || eqLong >= 50 ? (
    <div>
      <div className="icon-container">
        <TrendFire className="trendFire" />
        <LockPickTM />
      </div>
      <span className="trend-info">{`${t('app.eq.hot')}`}</span>
    </div>
  ) : (eqShort > -0.05 && eqShort < 0.2 && eqShort !== 0) || (eqLong > 0 && eqLong < 50) ? (
    <div>
      <div className="icon-container">
        <TrendUp className="trendUp" />
      </div>
      <span className="trend-info">{`${t('app.eq.rising')}`}</span>
    </div>
  ) : eqShort < -0.05 || eqLong < 0 ? (
    <div>
      <div className="icon-container">
        <TrendDown className="trendDown" />
      </div>
      <span className="trend-info">{`${t('app.eq.caution')}`}</span>
    </div>
  ) : (
    <div>
      <span className="trend-info"></span>
    </div>
  );
};

function PickLeg({ leg, pick, isDetail }: { leg: ApiPickLeg; pick: ApiPick; isDetail?: boolean }) {
  return (
    <div className={'pickCard__leg'}>
      <div className={'pickCard__leg-main'}>
        {leg.subject === 'athlete' && isDetail ? (
          <a
            href={`https://www.espn.com/${pick.sport}/player/gamelog/_/id/${leg.athleteId}`}
            target={'_tab'}
            className={'pickCard__leg-name'}
          >
            {leg.athleteName}
          </a>
        ) : (
          <span className={'pickCard__leg-name'}>{leg.athleteName}</span>
        )}
        {leg.subject === 'team' && (
          <span className={'pickCard__leg-name'}>
            {leg.teamAbbreviation != null ? `${leg.teamAbbreviation} Team Total` : `Any Team Total`}
          </span>
        )}
        <span className={'pickCard__leg-pick'}>
          {formatThreshold(leg.operator, leg.threshold, leg.statPath, pick.sport)}
        </span>
        <Last7Indicator series={leg.last7Series} games={leg.last7Games} hits={leg.last7Hits} />
        <span className={'pickCard__leg-eq'}>{getEqIndex(leg.eqShort, leg.eqLong)}</span>
      </div>
      <div className={'pickCard__leg-meta'}>
        <span className={'pickCard__leg-prob'}>{formatProbability(leg.legProbHigh)}</span>
      </div>
    </div>
  );
}

export default PickLeg;
